/**
 * V2.0.83 — Canvas pointer orchestrator.
 *
 * Consolidates the top-level SVG `onPointerMove` + `onPointerUp` logic
 * that used to live inline in FloorPlan2D.jsx. Owns the branching
 * between the four active interaction modes:
 *
 *   1. Wall drawing tool (delegated to `useWallDrawTool`)
 *   2. Place-drag positioning of a newly-added opening (delegated to
 *      `useOpeningDragMode`)
 *   3. Symbol resize (inline logic — anchored-corner geometry)
 *   4. Wall-attached opening drag (`openingDrag` — inline, drives
 *      position along the wall span)
 *   5. Symbol move (inline logic — cursor offset applied to x/z)
 *   6. Wall hover-preview highlight (fires only when 1-5 are inactive)
 *
 * On pointerup, flushes a single authoritative PUT via
 * `symbolDrag.pendingBlueprintRef` and clears the drag state atoms.
 * Emits telemetry for the terminated drag.
 *
 * This is the "last major monolith" extraction from FloorPlan2D per
 * the code-health backlog. FloorPlan2D now only owns rendering + the
 * click handler; every pointer-move / pointer-up branch lives here.
 */
import { useCallback } from "react";
import { snap as snapToGrid } from "../../lib/units";
import { findWallAtPoint } from "../../lib/openings";
import { emitInteractionEvent } from "../../lib/interactionTelemetry";

export default function useCanvasPointer({
  // rendering deps
  blueprint,
  rooms,
  measurementSystem,
  pointerToWorld,
  // sub-hooks
  wallTool,
  placeDrag,
  wallAddMenu,
  symbolDrag,
  // still-inline state atoms
  hoverWall,
  setHoverWall,
  openingDrag,
  setOpeningDrag,
  // helpers
  getCurrentSymbols,
  persistOpenings,
  onUpdateBlueprint,
}) {
  const handlePointerMove = useCallback(
    (e) => {
      // (1) Wall drawing tool — preview follows the cursor.
      if (wallTool.active) {
        wallTool.updatePreview(e);
        return;
      }
      // (2) Place-drag for a just-added opening.
      if (placeDrag.handlePointerMove(e)) {
        return;
      }
      // (3) Hover preview — active only when nothing else is dragging.
      const {
        moveSymbol,
        resizeOp,
        persistSymbols,
      } = symbolDrag;
      if (!resizeOp && !moveSymbol && !openingDrag && !wallAddMenu.menu) {
        const world = pointerToWorld(e);
        if (world) {
          const hit = findWallAtPoint(rooms || [], world.x_m, world.z_m, 0.5);
          if (hit) {
            const sig = `${hit.kind}|${hit.axis}|${hit.coord.toFixed(2)}|${hit.span_lo.toFixed(2)}|${hit.span_hi.toFixed(2)}`;
            if (!hoverWall || hoverWall._sig !== sig) {
              setHoverWall({ ...hit, _sig: sig });
            }
          } else if (hoverWall) {
            setHoverWall(null);
          }
        }
      } else if (hoverWall) {
        setHoverWall(null);
      }
      // (4) Symbol resize — corner-anchored, snapped to grid.
      if (resizeOp) {
        const world = pointerToWorld(e);
        if (!world) return;
        const list = getCurrentSymbols();
        const sym = list.find((s) => s.id === resizeOp.id);
        if (!sym) return;
        const minSize = 0.3;
        let new_w = resizeOp.startW_m;
        let new_d = resizeOp.startD_m;
        let new_x = sym.x;
        let new_z = sym.z;
        const dx = world.x_m - resizeOp.anchorWorld.x_m;
        const dz = world.z_m - resizeOp.anchorWorld.z_m;
        if (resizeOp.corner.includes("e")) {
          new_w = Math.max(minSize, resizeOp.startW_m + dx);
        } else {
          new_w = Math.max(minSize, resizeOp.startW_m - dx);
          new_x =
            resizeOp.anchorWorld.x_m -
            new_w +
            (resizeOp.startCx + resizeOp.startW_m - resizeOp.anchorWorld.x_m);
        }
        if (resizeOp.corner.includes("s")) {
          new_d = Math.max(minSize, resizeOp.startD_m + dz);
        } else {
          new_d = Math.max(minSize, resizeOp.startD_m - dz);
          new_z =
            resizeOp.anchorWorld.z_m -
            new_d +
            (resizeOp.startCz + resizeOp.startD_m - resizeOp.anchorWorld.z_m);
        }
        new_w = snapToGrid(new_w, measurementSystem);
        new_d = snapToGrid(new_d, measurementSystem);
        persistSymbols(
          list.map((s) =>
            s.id === resizeOp.id
              ? {
                  ...s,
                  x: snapToGrid(new_x, measurementSystem),
                  z: snapToGrid(new_z, measurementSystem),
                  override_w_m: new_w,
                  override_d_m: new_d,
                }
              : s,
          ),
        );
        return;
      }
      // (5) Wall-attached opening drag — sliding along the wall span.
      if (openingDrag) {
        const world = pointerToWorld(e);
        if (!world) return;
        const along = openingDrag.axis === "x" ? world.x_m : world.z_m;
        const halfLen = openingDrag.halfLen_m;
        const lo = openingDrag.span_lo + halfLen;
        const hi = openingDrag.span_hi - halfLen;
        const clamped = Math.min(hi, Math.max(lo, along));
        const newPosition =
          (clamped - openingDrag.span_lo) /
          (openingDrag.span_hi - openingDrag.span_lo);
        const key = openingDrag.kind === "door" ? "doors" : "windows";
        const list = openingDrag.list;
        const newList = list.map((entry, i) => {
          if (i !== openingDrag.idx) return entry;
          return {
            ...entry,
            position: Math.max(0, Math.min(1, newPosition)),
          };
        });
        persistOpenings(key, newList);
        return;
      }
      // (6) Symbol move — cursor offset applied to x/z.
      if (!moveSymbol) return;
      const world = pointerToWorld(e);
      if (!world) return;
      const snap = (v) => snapToGrid(v, measurementSystem);
      persistSymbols(
        getCurrentSymbols().map((s) =>
          s.id === moveSymbol.id
            ? {
                ...s,
                x: snap(world.x_m - moveSymbol.offsetX),
                z: snap(world.z_m - moveSymbol.offsetZ),
              }
            : s,
        ),
      );
    },
    [
      wallTool,
      placeDrag,
      wallAddMenu.menu,
      symbolDrag,
      openingDrag,
      hoverWall,
      setHoverWall,
      pointerToWorld,
      rooms,
      measurementSystem,
      getCurrentSymbols,
      persistOpenings,
    ],
  );

  const handlePointerUp = useCallback(() => {
    const { dragInFlightRef, pendingBlueprintRef, moveSymbol, resizeOp, setMoveSymbol, setResizeOp } = symbolDrag;
    const wasInFlight = dragInFlightRef.current;
    dragInFlightRef.current = false;
    if (wasInFlight && onUpdateBlueprint) {
      const flushTarget = pendingBlueprintRef.current || blueprint;
      if (flushTarget) {
        onUpdateBlueprint(JSON.parse(JSON.stringify(flushTarget)));
      }
    }
    pendingBlueprintRef.current = null;
    if (wasInFlight) {
      if (moveSymbol) {
        emitInteractionEvent({ tool: "symbol_drag", action: "move_commit", committed: true });
      } else if (resizeOp) {
        emitInteractionEvent({ tool: "symbol_drag", action: "resize_commit", committed: true });
      } else if (openingDrag) {
        emitInteractionEvent({
          tool: "opening_drag",
          action: "wall_position_commit",
          committed: true,
          meta: { kind: openingDrag.kind },
        });
      }
    }
    setMoveSymbol(null);
    setResizeOp(null);
    setOpeningDrag(null);
  }, [symbolDrag, openingDrag, setOpeningDrag, blueprint, onUpdateBlueprint]);

  return { handlePointerMove, handlePointerUp };
}
